/* eslint-disable @typescript-eslint/no-unused-vars */
import DialogProvider from "@components/dialog_provider"
import Switch from "@components/switch"
import { useCustomerPageContext } from "@contexts/customer"
import { updateCustomerProducts } from "@domain/customer/infos"
import { Stack, TextField, Typography } from "@mui/material"
import { showErrorToast } from "@utils/toast"
import { Features } from "__generated__/graphql"
import { useEffect, useState } from "react"

export default function FeatureDialog({
  isOpen,
  setClose
}: {
  isOpen: boolean
  setClose: () => void
}) {
  const { translate, currentCustomer: customer } = useCustomerPageContext()
  const [loading, setLoading] = useState(false)
  const [features, setFeatures] = useState<Features>()
  const [prevFeatures, setPrevFeatures] = useState<Features>()
  const [searchTerm, setSearchTerm] = useState<string>("")
  useEffect(() => {
    if (customer?.package?.features) {
      /* eslint-disable-next-line no-unsafe-optional-chaining */
      const { __typename, ...filteredFeatures } = customer?.package?.features
      setFeatures(filteredFeatures)
      setPrevFeatures(filteredFeatures)
    }
  }, [customer])

  function handleClose() {
    if (loading) {
      return
    }
    setClose()
  }

  async function handleSubmit() {
    if (!customer?.package?._id) {
      return
    }
    setLoading(true)
    const { isSuccess, errorMessage } = await updateCustomerProducts({
      _id: customer?.package?._id,
      features: features
    })
    if (!isSuccess && !!errorMessage) {
      showErrorToast(errorMessage)
      setLoading(false)
      return
    }

    setLoading(false)
    handleClose()
  }

  const handleSwitch = (key: string, value: boolean) => {
    if (features) {
      setFeatures({
        ...features,
        [key]: value
      })
    }
  }

  const filteredFeatures = features
    ? Object.entries(features).filter(([key]) =>
        key.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : []

  return (
    <DialogProvider
      title={translate("features")}
      leftButton={{
        text: translate("cancel"),
        color: "secondary",
        onClick: setClose,
        disabled: loading
      }}
      rightButton={{
        text: translate("save"),
        color: "primary",
        onClick: handleSubmit,
        loading: loading,
        disabled: JSON.stringify(prevFeatures) === JSON.stringify(features)
      }}
      isOpen={isOpen}
      setClose={handleClose}
      size="small"
    >
      <Stack gap={2}>
        <TextField
          label={translate("search_features")}
          variant="outlined"
          size="small"
          fullWidth
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1,
            backgroundColor: "background.paper"
          }}
        />
        <Stack sx={{ overflowY: "auto" }} gap={2} p={1}>
          {filteredFeatures.length > 0 ? (
            filteredFeatures.map(([key, value], index) => (
              <Stack
                key={index}
                flexDirection={"row"}
                justifyContent={"space-between"}
              >
                <Typography>{key}</Typography>
                <Switch
                  checked={value as boolean}
                  onChange={() => handleSwitch(key, !(value as boolean))}
                />
              </Stack>
            ))
          ) : (
            <Typography>{translate("no_features_available")}</Typography>
          )}
        </Stack>
      </Stack>
    </DialogProvider>
  )
}
